/**
 * Backend API Service
 * 
 * Handles REST API calls to the ClariMe backend.
 * Key flows:
 *   - Audio upload (multipart) → visit creation → transcription → summary
 *   - Status polling during processing
 *   - Visit CRUD, Care Groups, User profile
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// ==================== Core Request Helper ====================

/**
 * Make an authenticated API request
 * @param {string} endpoint - API endpoint (e.g. '/visits')
 * @param {object} options - Fetch options
 * @returns {Promise<any>} Response data
 */
async function apiRequest(endpoint, options = {}) {
  const token = localStorage.getItem('auth_token');

  const headers = { ...options.headers };

  // Don't set Content-Type for FormData (browser sets it with boundary)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    const err = new Error(error.message || error.error || `HTTP ${response.status}`);
    err.status = response.status;

    // A 401 on an authenticated, non-auth endpoint means the session token
    // the server saw is dead (expired, or the account was deleted). Announce
    // it so useAuth can route to a clean sign-in screen instead of letting
    // every screen dead-end on "Invalid or expired token". Auth endpoints
    // are excluded: a wrong password is also a 401 and must not sign the
    // user out of anything.
    if (response.status === 401 && token && !endpoint.startsWith('/auth/')) {
      window.dispatchEvent(new CustomEvent('clarime:session-expired'));
    }

    throw err;
  }

  return response.json();
}

// ==================== Visit Pipeline ====================

/**
 * Create a new visit with audio upload
 * 
 * This is the main pipeline entry point. Sends the audio blob as
 * multipart form data along with visit metadata. The backend will:
 *   1. Upload audio to S3
 *   2. Start AWS Transcribe Medical job
 *   3. Return a visitId for status polling
 * 
 * @param {Blob} audioBlob - Recorded audio from useAudioRecorder
 * @param {object} metadata - Visit metadata
 * @param {string} metadata.providerName - Doctor's name (optional, can be AI-extracted)
 * @param {string} metadata.specialty - Medical specialty (optional)
 * @param {number} metadata.durationSeconds - Recording duration in seconds
 * @returns {Promise<{visit: {id, status, transcriptionJobName}, message}>}
 */
export async function createVisitWithAudio(audioBlob, metadata = {}) {
  const formData = new FormData();

  // Determine file extension from blob type
  const mimeType = audioBlob.type || 'audio/webm';
  const extension = mimeType.split('/')[1]?.split(';')[0] || 'webm';
  const filename = `recording-${Date.now()}.${extension}`;

  // WKWebView (Capacitor iOS) has a known bug where fetch() with FormData
  // containing a Blob fails with "Load failed" because WebKit can't lazily
  // read the Blob data. Fix: materialize as ArrayBuffer → File first.
  let audioFile;
  try {
    const arrayBuffer = await audioBlob.arrayBuffer();
    audioFile = new File([arrayBuffer], filename, { type: mimeType });
  } catch {
    // Fallback for browsers that don't support blob.arrayBuffer()
    audioFile = new File([audioBlob], filename, { type: mimeType });
  }

  formData.append('audio', audioFile, filename);

  // Append metadata fields
  if (metadata.providerName) formData.append('providerName', metadata.providerName);
  if (metadata.specialty) formData.append('specialty', metadata.specialty);
  if (metadata.durationSeconds) formData.append('durationSeconds', String(metadata.durationSeconds));
  if (metadata.visitDate) formData.append('visitDate', metadata.visitDate);
  if (metadata.visitTime) formData.append('visitTime', metadata.visitTime);
  if (metadata.location) formData.append('location', metadata.location);

  return apiRequest('/visits', {
    method: 'POST',
    body: formData,
  });
}

/**
 * Poll visit processing status
 * 
 * The backend returns:
 *   - { status: 'transcribing', message: '...' }
 *   - { status: 'summarizing' }
 *   - { status: 'complete', summary: {...} }
 *   - { status: 'error', error: '...' }
 * 
 * @param {string} visitId - Visit ID from createVisitWithAudio
 * @returns {Promise<{status, summary?, error?}>}
 */
export async function getVisitStatus(visitId) {
  return apiRequest(`/visits/${visitId}/status`);
}

/**
 * Poll until visit processing completes
 * 
 * @param {string} visitId - Visit ID
 * @param {object} options
 * @param {function} options.onStatusChange - Called with each status update
 * @param {number} options.pollInterval - Ms between polls (default 3000)
 * @param {number} options.maxWait - Max ms to wait (default 10 min)
 * @returns {Promise<{status, summary}>} Resolves when complete or errors
 */
export async function pollUntilComplete(visitId, options = {}) {
  const {
    onStatusChange = () => {},
    pollInterval = 3000,
    maxWait = 10 * 60 * 1000, // 10 minutes
  } = options;

  const startTime = Date.now();

  while (Date.now() - startTime < maxWait) {
    try {
      const result = await getVisitStatus(visitId);
      onStatusChange(result.status, result);

      if (result.status === 'complete') {
        return result;
      }

      if (result.status === 'error') {
        throw new Error(result.error || 'Processing failed');
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    } catch (err) {
      // If it's a network error, keep retrying (server might be slow)
      if (err.message === 'Processing failed') {
        throw err;
      }
      console.warn('Poll error, retrying:', err.message);
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
  }

  throw new Error('Processing timed out — your summary may still be generating. Check back shortly.');
}

// ==================== Visits CRUD ====================

export async function createVisit(visitData) {
  return apiRequest('/visits', {
    method: 'POST',
    body: JSON.stringify(visitData),
  });
}

export async function getVisits() {
  return apiRequest('/visits');
}

export async function getVisit(visitId) {
  return apiRequest(`/visits/${visitId}`);
}

export async function updateVisit(visitId, updates) {
  return apiRequest(`/visits/${visitId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteVisit(visitId) {
  return apiRequest(`/visits/${visitId}`, {
    method: 'DELETE',
  });
}

export async function shareVisit(visitId, groupId) {
  return apiRequest(`/visits/${visitId}/share`, {
    method: 'POST',
    body: JSON.stringify({ groupId }),
  });
}

export async function refineSummary(visitId, feedback) {
  return apiRequest(`/visits/${visitId}/refine-summary`, {
    method: 'POST',
    body: JSON.stringify({ feedback }),
  });
}

// ==================== Care Groups ====================

export async function createGroup(groupData) {
  return apiRequest('/groups', {
    method: 'POST',
    body: JSON.stringify(groupData),
  });
}

export async function getGroups() {
  return apiRequest('/groups');
}

export async function getGroup(groupId) {
  return apiRequest(`/groups/${groupId}`);
}

export async function addGroupMembers(groupId, members, { suppressInviteEmail = false } = {}) {
  return apiRequest(`/groups/${groupId}/members`, {
    method: 'POST',
    body: JSON.stringify({ members, suppressInviteEmail }),
  });
}

export async function sendGroupMessage(groupId, text) {
  return apiRequest(`/groups/${groupId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

export async function getGroupMessages(groupId, options = {}) {
  const { limit = 50, before } = options;
  const params = new URLSearchParams({ limit });
  if (before) params.append('before', before);
  return apiRequest(`/groups/${groupId}/messages?${params}`);
}

/**
 * Load public context for a one-tap invite link (#/join/<token>):
 * group name, inviter name, and the invited email for pre-fill.
 * No auth required.
 */
export async function getInviteInfo(token) {
  return apiRequest(`/invites/${token}`);
}

/**
 * Redeem an invite token for the signed-in user — joins them to the
 * invite's Care Group even if they signed up with a different email
 * than the one invited. Idempotent.
 */
export async function acceptInvite(token) {
  return apiRequest(`/invites/${token}/accept`, { method: 'POST' });
}

export async function resendInvitation(groupId, inviteId) {
  return apiRequest(`/groups/${groupId}/invites/${inviteId}/resend`, {
    method: 'POST',
  });
}

export async function removeGroupMember(groupId, memberId) {
  return apiRequest(`/groups/${groupId}/members/${memberId}`, {
    method: 'DELETE',
  });
}

export async function updateInviteName(groupId, inviteId, name) {
  return apiRequest(`/groups/${groupId}/invites/${inviteId}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
}

export async function revokeGroupInvite(groupId, inviteId) {
  return apiRequest(`/groups/${groupId}/invites/${inviteId}`, {
    method: 'DELETE',
  });
}

export async function createMemberRequest(groupId, contact) {
  return apiRequest(`/groups/${groupId}/member-requests`, {
    method: 'POST',
    body: JSON.stringify({
      contactName: contact.name,
      contactEmail: contact.email,
      contactPhone: contact.phone,
    }),
  });
}

export async function approveMemberRequest(groupId, requestId) {
  return apiRequest(`/groups/${groupId}/member-requests/${requestId}/approve`, {
    method: 'POST',
  });
}

export async function denyMemberRequest(groupId, requestId) {
  return apiRequest(`/groups/${groupId}/member-requests/${requestId}/deny`, {
    method: 'POST',
  });
}

// ==================== User ====================

export async function getCurrentUser() {
  return apiRequest('/users/me');
}

export async function updateProfile(updates) {
  return apiRequest('/users/me', {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Permanently delete the current user's account.
 * Required by App Store Guideline 5.1.1(v).
 */
export async function deleteAccount() {
  return apiRequest('/users/me', { method: 'DELETE' });
}

// ==================== Push Notifications ====================

export async function registerPushToken(token, platform) {
  return apiRequest('/push/register', {
    method: 'POST',
    body: JSON.stringify({ token, platform }),
  });
}

export async function unregisterPushToken(token) {
  return apiRequest('/push/unregister', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

// ==================== Health Check ====================

/**
 * Check if the backend is reachable
 * Useful before starting a recording to warn the user
 */
export async function healthCheck() {
  try {
    const result = await apiRequest('/health');
    return { healthy: result.status === 'healthy', ...result };
  } catch {
    return { healthy: false, error: 'Cannot reach ClariMe server' };
  }
}

// ==================== Auth Helpers ====================

/**
 * Get a fresh JWT for the current session (authenticated).
 * Called on app open so the 90-day expiry is a sliding window.
 * Returns { idToken, email, firstName }.
 */
export async function refreshSession() {
  return apiRequest('/auth/email/refresh', { method: 'POST' });
}

/**
 * Set a password for the current user (authenticated)
 */
export async function setPassword(password) {
  return apiRequest('/auth/email/set-password', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

/**
 * Login with email + password (unauthenticated)
 */
export async function loginWithPassword(email, password) {
  return apiRequest('/auth/email/login-password', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

/**
 * Sign in with Apple (send identity token to backend)
 */
export async function socialAppleSignIn({ identityToken, fullName, email }) {
  return apiRequest('/auth/social/apple', {
    method: 'POST',
    body: JSON.stringify({ identityToken, fullName, email }),
  });
}

/**
 * Sign in with Google (send ID token to backend)
 */
export async function socialGoogleSignIn({ idToken }) {
  return apiRequest('/auth/social/google', {
    method: 'POST',
    body: JSON.stringify({ idToken }),
  });
}

export default {
  createVisitWithAudio,
  getVisitStatus,
  pollUntilComplete,
  createVisit,
  getVisits,
  getVisit,
  updateVisit,
  deleteVisit,
  shareVisit,
  refineSummary,
  createGroup,
  getGroups,
  addGroupMembers,
  removeGroupMember,
  updateInviteName,
  revokeGroupInvite,
  createMemberRequest,
  approveMemberRequest,
  denyMemberRequest,
  sendGroupMessage,
  getGroupMessages,
  resendInvitation,
  getInviteInfo,
  acceptInvite,
  getCurrentUser,
  updateProfile,
  deleteAccount,
  registerPushToken,
  unregisterPushToken,
  healthCheck,
  refreshSession,
  setPassword,
  loginWithPassword,
  socialAppleSignIn,
  socialGoogleSignIn,
};
